/**
 * vaultSave — shared utility for auto-saving generation results to the draft vault.
 * Deduplicates identical save logic from handleGenerate and handleRegenerateWithAdvice.
 */

import { ParsedSunoOutput } from '@/types';
import { API_ENDPOINTS } from '@/config/api';
import { fetchWithCsrf } from './fetchWithCsrf';
import { trimTagList } from '@services/llmShared';
import { pendingRouletteTag } from './studioRouletteStore';

// The server caps style/excludedStyle at 1000 chars to match what Suno
// actually accepts. Generation now trims to that limit too (see
// llmShared.ts/geminiService.ts), but takes generated before that fix (or
// edited by hand) can still be over -- trim again here so an already-over
// take doesn't get stuck unable to save.
export async function saveToVault(
  result: ParsedSunoOutput,
  sourceType: 'generated' | 'regenerated'
): Promise<{ id?: number; summary?: string | null }> {
  const response = await fetchWithCsrf(API_ENDPOINTS.drafts.create, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      title: result.title,
      artist: result.artist,
      summary: result.summary,
      styleBlueprint: result.styleBlueprint,
      style: trimTagList(result.style || '', 1000),
      excludedStyle: trimTagList(result.excludedStyle || '', 1000),
      lyricsWithTags: result.lyricsWithTags,
      lyricsAlone: result.lyricsAlone,
      originalData: JSON.stringify(result),
      sourceType,
      // Studio Roulette: a song saved after "Load into tabs" carries that roll's mode and seed (AUTO-085).
      tags: sourceType === 'generated' ? (pendingRouletteTag() ?? undefined) : undefined,
      llmModel: result.llmModel || undefined,
      llmProvider: result.llmProvider || undefined,
    }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.error || `Save failed (${response.status})`);
  }
  localStorage.setItem('hookhouse_has_saved_vault', 'true');
  window.dispatchEvent(new CustomEvent('hookhouse:vault-updated', { detail: { draftId: data.id } }));
  return data;
}

/**
 * Persist an in-place edit to an already-saved draft (Forgejo #106). Used by
 * OutputSection's per-field edit UI -- PUTs only the changed field(s) via the
 * existing partial-update route rather than resending the whole draft.
 * Throws on failure so callers can keep their edit UI open and show the real
 * error instead of a false "saved" state.
 */
export async function updateDraftField(
  draftId: string,
  data: Record<string, string>
): Promise<void> {
  const response = await fetchWithCsrf(API_ENDPOINTS.drafts.update(draftId), {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    const errData = await response.json().catch(() => ({}));
    throw new Error(errData.error || `Save failed (${response.status})`);
  }
}
